import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, AlertCircle, CheckCircle2, XCircle, Clock, Gem, Info, CreditCard, Building2, User } from 'lucide-react'
import useUzsWithdrawStore, { createCardWithdrawStore } from '../store/useUzsWithdrawStore'
import useAppStore from '../../../store/useAppStore'
import toast from 'react-hot-toast'

const useTjsWithdrawStore = createCardWithdrawStore('tjs')

/**
 * UzsWithdraw — форма вывода кристаллов на карту в UZS или TJS
 * Содержит поля карты, ФИО, кристаллов (банк вводится только для UZS),
 * блок активной заявки, расчёт суммы и историю
 */
const UzsWithdraw = ({ currency = 'uzs' }) => {
  const isTjs = currency === 'tjs'
  const { t } = useTranslation()
  const { balance, loadProfile } = useAppStore()
  const { info, history, isLoading, isSubmitting, error, loadData, submitCardWithdraw, stopPolling } = (isTjs ? useTjsWithdrawStore : useUzsWithdrawStore)()
  const label = (key, options) => isTjs
    ? t(`withdraw.tjs.${key}`, { defaultValue: t(`withdraw.uzs.${key}`, options), ...(typeof options === 'object' ? options : {}) })
    : t(`withdraw.uzs.${key}`, options)
  const amountKey = isTjs ? 'tjs' : 'uzs'

  const [cardNumber, setCardNumber] = useState('')
  const [cardOwner, setCardOwner] = useState('')
  const [bankName, setBankName] = useState('')
  const [crystals, setCrystals] = useState('')
  const [validationError, setValidationError] = useState(null)

  // Загрузка данных при монтировании
  useEffect(() => {
    loadData().catch(() => {})
    return () => stopPolling()
  }, [loadData, stopPolling])

  // Подставляем реквизиты последней успешной выплаты при загрузке
  useEffect(() => {
    if (info?.last_card_number) setCardNumber(info.last_card_number)
    if (info?.last_card_owner) setCardOwner(info.last_card_owner)
    if (info?.last_bank_name) setBankName(info.last_bank_name)
  }, [info?.last_card_number, info?.last_card_owner, info?.last_bank_name])

  // Live-расчёт суммы в валюте карты
  // Формула из документации: amount = Math.floor(crystals * 1000 / crystals_per_1000_currency_net)
  const rate = info?.[isTjs ? 'crystals_per_1000_tjs_net' : 'crystals_per_1000_uzs_net']
  const payoutAmount = useMemo(() => {
    const crystalsNum = Number(crystals)
    if (!Number.isSafeInteger(crystalsNum) || !rate || rate <= 0) return 0
    return Math.floor(crystalsNum * 1000 / rate)
  }, [crystals, rate])

  // Форматирование номера карты с пробелами каждые 4 цифры
  const formatCardNumber = (value) => {
    const digits = value.replace(/\D/g, '')
    return digits.replace(/(.{4})/g, '$1 ').trim().slice(0, 23) // 19 цифр + пробелы
  }

  // Валидация формы
  const validate = () => {
    const cardDigits = cardNumber.replace(/\D/g, '')
    if (cardDigits.length < 16 || cardDigits.length > 19) {
      setValidationError(label('errors.invalidCard', 'Проверьте номер карты (16 цифр)'))
      return false
    }

    // ФИО: минимум два слова, 3–64 символа, только буквы/пробел/дефис/апостроф/точка
    const ownerRegex = /^[\p{L}\p{M}\s'.\-\u200c]{3,64}$/u
    if (!cardOwner || cardOwner.trim().split(/\s+/).length < 2) {
      setValidationError(label('errors.invalidOwner', 'Укажите ФИО владельца карты полностью'))
      return false
    }
    if (!ownerRegex.test(cardOwner)) {
      setValidationError(label('errors.invalidOwner', 'Укажите ФИО владельца карты полностью'))
      return false
    }

    // Банк: 2–64 символа
    const bankRegex = /^[\p{L}\p{M}0-9\s.,&\-\u200c]{2,64}$/u
    if (!isTjs && (!bankName || !bankRegex.test(bankName))) {
      setValidationError(label('errors.invalidBank', 'Укажите банк получателя'))
      return false
    }

    const crystalsNum = Number(crystals)
    if (!crystals || !Number.isSafeInteger(crystalsNum) || crystalsNum < (info?.min_crystals || 0)) {
      setValidationError(label('minAmount', 'Минимальная сумма') + ': ' + (info?.min_crystals?.toLocaleString('en-US') || ''))
      return false
    }
    if (crystalsNum > (info?.max_crystals || 0)) {
      setValidationError(label('maxAmount', 'Максимальная сумма') + ': ' + (info?.max_crystals?.toLocaleString('en-US') || ''))
      return false
    }
    if (isTjs && (payoutAmount < info?.min_tjs || payoutAmount > info?.max_tjs)) {
      setValidationError(label('errors.errorSum', { min: info.min_tjs, max: info.max_tjs }))
      return false
    }
    if (crystalsNum > balance) {
      setValidationError(label('insufficientBalance', 'Недостаточно кристаллов'))
      return false
    }

    setValidationError(null)
    return true
  }

  // Обработка отправки формы
  const handleSubmit = async (e) => {
    e?.preventDefault()

    if (!validate()) return

    try {
      const result = await submitCardWithdraw({
        card_number: cardNumber,
        card_owner: cardOwner,
        ...(isTjs ? {} : { bank_name: bankName }),
        crystals: Number(crystals),
      })

      // Если сумма из ответа отличается от показанной — сообщаем
      if (result[amountKey] && result[amountKey] !== payoutAmount) {
        toast.success(isTjs
          ? label('successWithAdjustedAmount', { amount: result[amountKey].toLocaleString('en-US') })
          : label('successWithAdjustedAmount', {
          defaultValue: 'Заявка создана! Сумма: {{uzs}} UZS',
          uzs: result.uzs.toLocaleString('en-US'),
        }))
      } else {
        toast.success(label('success', 'Заявка на вывод создана!'))
      }

      // Обновляем баланс — кристаллы списываются сразу
      await loadProfile()

      // Очищаем поле суммы
      setCrystals('')
    } catch (error) {
      if (error.isNotified || error.isHandledAuthError) return
      // Обработка кодов ошибок из документации §5
      const errorMsg = error.message || ''
      const errorMap = {
        'disabled': label('errors.disabled', 'Вывод в UZS временно недоступен'),
        'withdraw banned': label('errors.withdrawBanned', 'Вывод заблокирован, обратитесь в поддержку'),
        [`${currency} deposit required`]: label(isTjs ? 'errors.tjsDepositRequired' : 'errors.uzsDepositRequired', 'Вывод на карту доступен после пополнения в сумах'),
        'active request': label('errors.activeRequest', 'Дождитесь завершения текущей заявки'),
        'invalid card': label('errors.invalidCard', 'Проверьте номер карты (16 цифр)'),
        'invalid owner': label('errors.invalidOwner', 'Укажите ФИО владельца карты полностью'),
        ...(!isTjs ? { 'invalid bank': label('errors.invalidBank', 'Укажите банк получателя') } : {}),
        'error sum': isTjs
          ? label('errors.errorSum', { min: info?.min_tjs, max: info?.max_tjs })
          : label('errors.errorSum', 'Сумма вне лимитов'),
        'insufficient balance': label('errors.insufficientBalance', 'Недостаточно кристаллов'),
        'error backend': label('errors.errorBackend', 'Техническая ошибка, попробуйте позже'),
      }

      const matchedMsg = Object.entries(errorMap).find(([key]) => errorMsg.includes(key))
      toast.error(matchedMsg ? matchedMsg[1] : label('errors.errorBackend', 'Техническая ошибка, попробуйте позже'))
    }
  }

  // Проверка блокировки формы
  const isFormDisabled = !info?.enabled || !info?.can_withdraw || info?.has_active || isSubmitting || (isTjs && (!rate || rate <= 0))
  // Причина блокировки формы
  const isBlockedReason = !info?.can_withdraw
    ? (info?.[isTjs ? 'has_tjs_deposit' : 'has_uzs_deposit'] === false
        ? label('blockedNoDeposit', 'Вывод на карту доступен после пополнения в сумах.')
        : label('blockedCannotWithdraw', 'Вывод недоступен. Обратитесь в поддержку.'))
    : info?.has_active
      ? label('blockedHasActive', 'У вас уже есть активная заявка. Дождитесь её завершения.')
      : null

  // Конфигурация статусов для истории
  const getStatusConfig = (status) => {
    switch (status) {
      case 'new':
        return {
          icon: Clock,
          iconClass: 'text-blue-400',
          textClass: 'text-blue-400',
          label: label('status.new', 'Ждёт проверки'),
          containerClass: 'border-blue-500/20 bg-blue-500/5',
        }
      case 'approved':
      case 'sending':
      case 'waiting':
        return {
          icon: Loader2,
          iconClass: 'text-blue-400 animate-spin',
          textClass: 'text-blue-400',
          label: label('status.processing', 'В обработке'),
          containerClass: 'border-blue-500/20 bg-blue-500/5',
        }
      case 'success':
        return {
          icon: CheckCircle2,
          iconClass: 'text-accent-primary',
          textClass: 'text-accent-primary',
          label: label('status.success', 'Выплачено'),
          containerClass: 'border-accent-primary/20 bg-accent-primary/5',
        }
      case 'rejected':
        return {
          icon: XCircle,
          iconClass: 'text-red-400',
          textClass: 'text-red-400',
          label: label('status.rejected', 'Отклонена'),
          containerClass: 'border-red-500/20 bg-red-500/5',
        }
      case 'cancelled':
        return {
          icon: XCircle,
          iconClass: 'text-red-400',
          textClass: 'text-red-400',
          label: label('status.cancelled', 'Отменена, кристаллы возвращены'),
          containerClass: 'border-red-500/20 bg-red-500/5',
        }
      case 'stuck':
        return {
          icon: AlertCircle,
          iconClass: 'text-yellow-400',
          textClass: 'text-yellow-400',
          label: label('status.stuck', 'На проверке — заявка разбирается вручную'),
          containerClass: 'border-yellow-500/20 bg-yellow-500/5',
        }
      default:
        return {
          icon: Clock,
          iconClass: 'text-text-secondary',
          textClass: 'text-text-secondary',
          label: status,
          containerClass: 'border-white/10 bg-surface',
        }
    }
  }

  // Активная заявка из истории (первая с не-финальным статусом)
  const activeRequest = history.find((h) => ['new', 'approved', 'sending', 'waiting', 'stuck'].includes(h.status))

  if (isLoading && !info) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={32} className="text-accent-primary animate-spin" />
      </div>
    )
  }

  if (error && !info) {
    return (
      <div className="text-center py-16 text-text-secondary">
        <p>{label('errors.errorBackend', 'Техническая ошибка, попробуйте позже')}</p>
        <button type="button" onClick={() => loadData().catch(() => {})} className="mt-4 text-accent-primary active:scale-[0.98]">
          {label('retry', 'Повторить')}
        </button>
      </div>
    )
  }

  if (!info?.enabled) {
    return (
      <div className="text-center py-16">
        <AlertCircle size={48} className="mx-auto text-text-secondary mb-4 opacity-50" />
        <p className="text-text-secondary">{label('disabled', 'Вывод в UZS временно недоступен')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Блок активной заявки */}
      {activeRequest && (
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4">
          <div className="flex items-center gap-3 mb-2">
            <Loader2 size={20} className="text-blue-400 animate-spin" />
            <span className="font-semibold text-blue-400">
              {label('activeRequest', 'Активная заявка')} <bdi dir="ltr">#{activeRequest.id}</bdi>
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-secondary">
              <bdi dir="ltr">{isTjs ? activeRequest.card_number : activeRequest.card_mask}</bdi> · <bdi dir="auto">{activeRequest.bank_name}</bdi>
            </span>
            <span className="font-bold text-white">
              <bdi dir="ltr">{activeRequest[amountKey].toLocaleString('en-US')} {currency.toUpperCase()}</bdi>
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1 text-xs text-text-secondary">
            <Gem size={12} className="inline text-accent-primary" />
            <bdi dir="ltr">{activeRequest.crystals.toLocaleString('en-US')}</bdi>
          </div>
          <p className="text-xs text-text-secondary mt-2">
            {label('activeRequestDesc', 'Заявка обрабатывается. Уведомление о завершении придёт в Telegram.')}
          </p>
        </div>
      )}

      {/* Форма вывода */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Номер карты */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            {label('cardNumber', 'Номер карты')}
          </label>
          <div className="relative">
            <CreditCard size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-primary" />
            <input
              type="text"
              inputMode="numeric"
              dir="ltr"
              value={cardNumber}
              onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
              disabled={isFormDisabled}
              placeholder={isTjs ? '4444 5555 6666 7777' : '8600 1234 1234 1234'}
              className="w-full bg-dark-primary border border-white border-opacity-10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
            />
          </div>
          {isTjs && (
            <div className="flex items-start gap-2 mt-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm text-yellow-300" role="alert">
              <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
              <span>{label('bankWarning', { bank: t('withdraw.tjs.bankName') })}</span>
            </div>
          )}
        </div>

        {/* ФИО владельца карты */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            {label('cardOwner', 'ФИО владельца карты')}
          </label>
          <div className="relative">
            <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-primary" />
            <input
              type="text"
              value={cardOwner}
              dir="auto"
              onChange={(e) => setCardOwner(e.target.value)}
              disabled={isFormDisabled}
              placeholder="IVANOV IVAN"
              maxLength={64}
              className="w-full bg-dark-primary border border-white border-opacity-10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
            />
          </div>
        </div>

        {/* Банк получателя */}
        {!isTjs && <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            {label('bankName', 'Банк получателя')}
          </label>
          <div className="relative">
            <Building2 size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-primary" />
            <input
              type="text"
              value={bankName}
              dir="auto"
              onChange={(e) => setBankName(e.target.value)}
              disabled={isFormDisabled}
              placeholder="Kapitalbank"
              maxLength={64}
              className="w-full bg-dark-primary border border-white border-opacity-10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
            />
          </div>
        </div>}

        {/* Сумма в кристаллах */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            {label('amount', 'Сумма (кристаллы)')}
          </label>
          <div className="relative">
            <Gem size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-primary" />
            <input
              type="text"
              inputMode="numeric"
              dir="ltr"
              value={crystals ? Number(crystals).toLocaleString('en-US') : ''}
              onChange={(e) => setCrystals(e.target.value.replace(/\D/g, ''))}
              disabled={isFormDisabled}
              placeholder={info?.min_crystals?.toLocaleString('en-US') || '0'}
              className="w-full bg-dark-primary border border-white border-opacity-10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
            />
          </div>
          {isTjs && <p className="mt-2 text-xs text-text-secondary">{label('limits', { min: info?.min_tjs, max: info?.max_tjs })}</p>}
          <div className="mt-2 flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={() => info?.min_crystals && setCrystals(String(info.min_crystals))}
              disabled={isFormDisabled}
              className="text-text-secondary hover:text-accent-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {label('min', 'Мин.')}: {info?.min_crystals?.toLocaleString('en-US') || '—'}
            </button>
            <button
              type="button"
              onClick={() => info?.max_crystals && setCrystals(String(Math.min(info.max_crystals, balance)))}
              disabled={isFormDisabled}
              className="text-text-secondary hover:text-accent-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {label('max', 'Макс.')}: {info?.max_crystals?.toLocaleString('en-US') || '—'}
            </button>
          </div>
        </div>

        {/* Расчёт суммы в UZS */}
        {payoutAmount > 0 && (
          <div className="rounded-lg bg-accent-primary/10 border border-accent-primary/20 p-4 text-center">
            <p className="text-sm text-text-secondary mb-1">{label('youWillReceive', 'Вы получите')}</p>
            <p className="text-3xl font-bold text-accent-primary">
              <bdi dir="ltr">{payoutAmount.toLocaleString('en-US')} <span className="text-xl">{currency.toUpperCase()}</span></bdi>
            </p>
            <p className="text-xs text-text-secondary mt-1">
              {label('commissionIncluded', 'Комиссия сервиса включена в курс')}
            </p>
          </div>
        )}

        {/* Предупреждение о проверке админом */}
        <div className={`flex items-start gap-2 text-xs text-yellow-400 rounded-lg bg-yellow-500/10 border border-yellow-500/20 p-3 ${isTjs ? '' : 'hidden'}`}>
          <Info size={14} className="flex-shrink-0 mt-0.5" />
          <span>{label('approvalWarning', 'Кристаллы списываются сразу при создании заявки. Выплата на карту стартует только после ручного одобрения администратором.')}</span>
        </div>

        {/* Ошибка валидации */}
        {validationError && (
          <div className="flex items-center gap-2 text-sm text-red-400">
            <AlertCircle size={16} />
            <span>{validationError}</span>
          </div>
        )}

        {/* Причина блокировки формы */}
        {isBlockedReason && (
          <div className="flex items-center gap-2 text-sm text-yellow-400 rounded-lg bg-yellow-500/10 border border-yellow-500/20 p-3">
            <AlertCircle size={16} />
            <span>{isBlockedReason}</span>
          </div>
        )}

        {/* Кнопка отправки */}
        <button
          type="submit"
          disabled={isFormDisabled}
          className="w-full py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              {label('submitting', 'Создание заявки...')}
            </>
          ) : (
            label('submit', 'Вывести на карту')
          )}
        </button>
      </form>

      {/* История заявок */}
      {history.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-3">
            {label('historyTitle', 'История выводов')}
          </h3>
          <div className="space-y-2">
            {history.map((item) => {
              const statusConfig = getStatusConfig(item.status)
              const StatusIcon = statusConfig.icon

              return (
                <div
                  key={item.id}
                  className={`rounded-lg border p-3 ${statusConfig.containerClass}`}
                >
                  <div className="flex items-center gap-3">
                    <StatusIcon size={18} className={statusConfig.iconClass} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <bdi dir="ltr" className="text-sm font-medium text-white">#{item.id}</bdi>
                        <span className={`text-xs text-text-secondary ${isTjs ? 'break-all' : 'truncate'}`}><bdi dir="ltr">{isTjs ? item.card_number : item.card_mask}</bdi> · <bdi dir="auto">{item.bank_name}</bdi></span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-text-secondary">
                          <bdi dir="ltr">{item.crystals.toLocaleString('en-US')} <Gem size={12} className="inline text-accent-primary" /> → {item[amountKey].toLocaleString('en-US')} {currency.toUpperCase()}</bdi>
                        </span>
                      </div>
                      {item.card_owner && (
                        <div className="text-xs text-text-secondary mt-0.5"><bdi dir="auto">{item.card_owner}</bdi></div>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-white border-opacity-5">
                    <div className={`text-xs font-medium ${statusConfig.textClass}`}>
                      {statusConfig.label}
                      {item.status === 'success' && item.finished_at && (
                        <span className="text-text-secondary ms-2">· <bdi dir="auto">{item.finished_at}</bdi></span>
                      )}
                    </div>
                    {item.comment && item.status === 'rejected' && (
                      <div className="text-xs text-red-400 mt-1 break-words">
                        {label('commentLabel', 'Комментарий')}: <bdi dir="auto">{item.comment}</bdi>
                      </div>
                    )}
                    {item.error && item.status === 'cancelled' && (
                      <div className="text-xs text-red-400 mt-1 break-words">
                        <bdi dir="auto">{item.error}</bdi>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default UzsWithdraw
